import {
  chakra,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Input,
  SimpleGrid,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { DatePicker } from "@/ui/datePickers/DatePicker";
import { LuScanLine, LuSearch, LuTag } from "react-icons/lu";
import { Select } from "@/ui/selects/Select";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useGetActivityLabels } from "@/shared/core-activity-labels";
import { useGetBaseEquipmentInstances } from "@/shared/core-equipment-instances";
import { useGetKits } from "@/shared/core-product-kits";
import { ModalProductScanner } from "../components/ModalProductScanner";
import { ModalSearchProduct } from "@/core/products/components/ModalSearchProduct";
import {
  ProductsTable,
  ProductState,
} from "@/core/products/components/ProductsTable";
import { Controller, useForm } from "react-hook-form";
import { voucherSchema } from "../schemas/voucherSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useGetVoucherTypes } from "@/core/voucherTypes/useCases/useGetVoucherTypes";
import { useGetStorage } from "@/core/storages/useCases/useGetStorage";
import { useGetWorkOrder } from "@/core/workOrders/useCases/useGetWorkOrder";
import { useGetApplicants } from "@/core/applicants/useCases/useGetApplicants";
import { useGetCostCenters } from "@/core/costCenter/useCases/getCostCenters";

import { useAuth } from "@/shared/session";
import { SingleValue } from "react-select";
import { Voucher } from "@/shared/core-vouchers";
import { getDefaultValues } from "../consts";
import { useGetAliasProducts } from "@/core/aliasProduct/useCases/useGetAliasProducts";
import { useGetClientEntities } from "@/core/clientEntities/useCases/useGetClientEntities";
import { Field } from "@/ui/chakra/field";
import {
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "@/ui/chakra/popover";
import { VoucherSummaryDialog } from "./VoucherSummaryDialog";

export interface VoucherFormProps {
  onSubmit: (data: z.infer<typeof voucherSchema>) => Promise<void>;
  selectedVoucher?: Voucher | undefined;
  sumbitButtonText?: string;
  variant?: "page" | "modal";
  onCancel?: () => void;
  confirmBeforeSubmit?: boolean;
}

const formatCLP = (value: number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(value);

interface FormSectionProps {
  title: string;
  hint?: string;
  children: React.ReactNode;
  compact?: boolean;
  actions?: React.ReactNode;
}

const FormSection = ({
  title,
  hint,
  children,
  compact,
  actions,
}: FormSectionProps) => (
  <Grid
    templateColumns={compact ? "1fr" : { base: "1fr", lg: "260px 1fr" }}
    gap={compact ? 3 : { base: 4, lg: 10 }}
    px={compact ? 0 : { base: 4, md: 8 }}
    py={compact ? 4 : 7}
    borderBottomWidth="1px"
    borderColor="border"
    _last={{ borderBottomWidth: 0 }}
  >
    <Flex
      direction={compact ? "row" : "column"}
      align={compact ? "center" : "flex-start"}
      justify="space-between"
      gap={compact ? 4 : 1}
    >
      <Box>
        <Text fontWeight="semibold" color="fg">
          {title}
        </Text>
        {hint && (
          <Text fontSize="sm" color="fg.muted" mt={0.5}>
            {hint}
          </Text>
        )}
      </Box>
      {compact && actions}
    </Flex>
    <Box minW={0}>
      {!compact && actions && (
        <Flex justify="flex-end" mb={4}>
          {actions}
        </Flex>
      )}
      {children}
    </Box>
  </Grid>
);

export const VoucherForm = ({
  onSubmit,
  selectedVoucher,
  sumbitButtonText = "Crear vale",
  variant = "page",
  onCancel,
  confirmBeforeSubmit = false,
}: VoucherFormProps) => {
  const compact = variant === "modal";
  const [scannerModal, setScannerModal] = useState(false);
  const [searchProductModal, setSearchProductModal] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const [deliverName, setDeliverName] = useState<string>(
    selectedVoucher?.delivers?.name || "",
  );

  const { session } = useAuth();

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    control,
    setValue,
    getValues,
    watch,
    reset,
    resetField,
  } = useForm<z.infer<typeof voucherSchema>>({
    resolver: zodResolver(voucherSchema),
    defaultValues: {
      zone: session?.zone ?? undefined,
      voucherDate: new Date(),
    },
  });

  const { workOrder: workOrderValue, storage: storageValue } = watch();

  const { data: clientEntities } = useGetClientEntities({
    zoneIds: session?.zone ?? "",
  });

  const { data: voucherTypes } = useGetVoucherTypes(session?.zone ?? "");
  const { data: storages } = useGetStorage(session?.zone ?? "");
  const { data: workOrder } = useGetWorkOrder({
    zoneIds: session?.zone ?? undefined,
  });

  const { data: applicants } = useGetApplicants({
    zoneIds: session?.zone ?? "",
  });
  const { data: activityLabels } = useGetActivityLabels({
    zoneIds: session?.zone ?? "",
  });
  const { data: costCenters } = useGetCostCenters(session?.zone ?? "");
  const { data: baseEquipmentInstances } = useGetBaseEquipmentInstances({
    zoneIds: session?.zone ?? "",
  });

  const { data: kits } = useGetKits({
    storageId: storageValue?.value || undefined,
  });
  const { data: aliasProducts } = useGetAliasProducts({
    zoneIds: session?.zone ?? "",
  });

  const [selectedProducts, setSelectedProducts] = useState<ProductState[]>([]);

  useEffect(() => {
    if (!selectedVoucher) return;
    if (!session || !session.zone) return;
    reset(getDefaultValues({ ...selectedVoucher, zone: session.zone }));
    setSelectedProducts(
      selectedVoucher.products.map((p) => ({
        data: p,
        amount: Number(p.voucherProduct?.amount) ?? 1,
        missingStock: p.voucherProduct?.missingStock || undefined,
        comment: p.voucherProduct?.comment || "",
        price: p.voucherProduct?.price || undefined,
      })) || [],
    );
  }, [selectedVoucher, reset, session]);

  useEffect(() => {
    if (!storages?.length) return;
    if (!selectedVoucher) {
      setValue("storage", {
        value: storages[0].id,
        label: storages[0].name,
      });
      setDeliverName(storages[0].company.name);
      return;
    }
    const storage = storages.find((s) => s.id === selectedVoucher.storage?.id);
    if (storage) setDeliverName(storage.company.name);
  }, [storages, setValue, selectedVoucher]);

  const products = watch("products");

  const handleAddProduct = (product: ProductState) => {
    const products = getValues("products") || [];
    const productExists = products.find((p) => p.id === product.data.id);
    const isErpProduct = product.data.externalProduct ? false : true;
    if (productExists) {
      setSelectedProducts(
        selectedProducts.filter((p) => p.data.id !== product.data.id),
      );
      return setValue(
        "products",
        products.filter((p) => p.id !== product.data.id),
      );
    }
    const productToAdd = {
      id: product.data.id,
      amount: product.amount || 1,
      missingStock: product.missingStock,
      price: isErpProduct ? 0 : undefined,
    };

    setValue("products", [...products, productToAdd]);
    setSelectedProducts([
      ...selectedProducts,
      {
        data: product.data,
        ...productToAdd,
      },
    ]);
  };

  const handleAddMultipleProducts = (productsToAdd: ProductState[]) => {
    const currentProducts = getValues("products") || [];
    const currentSelectedProducts = [...selectedProducts];

    const newProducts: Array<{
      id: string;
      amount: number | string;
      missingStock?: number;
      price?: number;
    }> = [];
    const newSelectedProducts: ProductState[] = [];

    productsToAdd.forEach((product) => {
      const productExists = currentProducts.find(
        (p) => p.id === product.data.id,
      );

      if (!productExists) {
        const isErpProduct = product.data.externalProduct ? false : true;
        const productToAdd = {
          id: product.data.id,
          amount: product.amount || 1,
          missingStock: product.missingStock,
          price: isErpProduct ? 0 : undefined,
        };

        newProducts.push(productToAdd);
        newSelectedProducts.push({
          data: product.data,
          ...productToAdd,
        });
      }
    });

    setValue("products", [...currentProducts, ...newProducts]);
    setSelectedProducts([...currentSelectedProducts, ...newSelectedProducts]);
  };

  const handleEditProduct = (products: ProductState[]) => {
    setValue(
      "products",
      products.map((p) => ({
        id: p.data.id,
        amount: p.amount,
        missingStock: p.missingStock,
        comment: p.comment,
        price: p.price,
      })),
    );
    setSelectedProducts(products);
  };

  const handleScanProduct = async (product: { id: string }) => {
    const productExists = products.find((p) => p.id === product.id);
    if (productExists) return;
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({
        ...getDefaultValues(undefined),
        zone: session?.zone ?? undefined,
      });
      setSelectedProducts([]);
      setDeliverName("");
    }
  }, [isSubmitSuccessful, reset, session]);

  const handleOnChangeWorkOrder = (
    e: SingleValue<{
      value: string;
      label: string;
    }>,
  ) => {
    const workOrderFiltered = workOrder.find((wo) => wo.id === e?.value);

    if (!workOrderFiltered) return resetField("clientEntity");

    setValue("clientEntity", {
      value: workOrderFiltered.clientEntity?.id,
      label: workOrderFiltered.clientEntity?.name,
    });
  };

  const handleOnChangeStorage = (
    e: SingleValue<{
      value: string;
      label: string;
    }>,
  ) => {
    const storageFiltered = storages.find((s) => s.id === e?.value);

    if (!storageFiltered) return;

    setDeliverName(storageFiltered.company.name);
    resetField("clientEntity");
    resetField("costCenter");
    resetField("productApplicant");
    resetField("workOrder");
    resetField("activityLabel");
    resetField("equipmentInstance");

    resetField("products");
    setSelectedProducts([]);
  };

  const total = useMemo(
    () =>
      selectedProducts.reduce(
        (acc, p) => acc + (Number(p.amount) || 0) * (Number(p.price) || 0),
        0,
      ),
    [selectedProducts],
  );

  const fieldColumns = compact
    ? { base: 1, md: 2, lg: 3 }
    : { base: 1, md: 2, "2xl": 3 };

  const submitForm = handleSubmit(onSubmit);
  const openSummary = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = await trigger();
    if (isValid) setSummaryOpen(true);
  };
  const handleConfirmSummary = async () => {
    setSummaryOpen(false);
    await submitForm();
  };

  return (
    <Fragment>
      <chakra.form
        onSubmit={confirmBeforeSubmit ? openSummary : submitForm}
        display={compact ? undefined : "flex"}
        flexDirection="column"
        flex={compact ? undefined : 1}
      >
        <Box
          bg="bg.panel"
          borderWidth={compact ? 0 : "1px"}
          borderColor="border"
          rounded="xl"
          shadow={compact ? "none" : "xs"}
          mb={compact ? 0 : 6}
        >
          <FormSection
            title="Datos del vale"
            hint="Tipo y fecha de emisión."
            compact={compact}
          >
            <SimpleGrid columns={fieldColumns} gap={5}>
              <Field
                label="Tipo de vale"
                required
                invalid={!!errors.voucherType}
                errorText={errors.voucherType?.message?.toString()}
              >
                <Controller
                  name="voucherType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={voucherTypes?.map((vt) => ({
                        value: vt.id,
                        label: vt.name,
                      }))}
                      placeholder="Selecciona tipo de vale"
                      isSearchable={false}
                    />
                  )}
                />
              </Field>

              <Field
                label="Fecha"
                required
                invalid={!!errors.voucherDate}
                errorText={errors.voucherDate?.message?.toString()}
              >
                <Controller
                  name="voucherDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      maxDate={new Date()}
                      placeHolder="Seleccione la fecha"
                    />
                  )}
                />
              </Field>
            </SimpleGrid>
          </FormSection>

          <FormSection
            title="Origen y destino"
            hint="De qué bodega sale y quién lo recibe."
            compact={compact}
          >
            <SimpleGrid columns={fieldColumns} gap={5}>
              <Field
                label="Bodega"
                required
                invalid={!!errors.storage}
                errorText={errors.storage?.message?.toString()}
              >
                <Controller
                  name="storage"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Selecciona bodega"
                      options={storages?.map((s) => ({
                        value: s.id,
                        label: s.name,
                      }))}
                      onChange={(value) => {
                        field.onChange(value);
                        handleOnChangeStorage(value);
                      }}
                    />
                  )}
                />
              </Field>

              <Field label="Empresa que entrega" required>
                <Input
                  value={deliverName}
                  readOnly
                  bg="bg.subtle"
                  placeholder="Empresa que entrega"
                />
              </Field>

              <Field
                label="Centro de costo"
                required
                invalid={!!errors.costCenter}
                errorText={errors.costCenter?.message?.toString()}
              >
                <Controller
                  name="costCenter"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value}
                      placeholder="Selecciona centro de costo"
                      options={costCenters
                        ?.filter((cc) => cc.enabled)
                        .map((cc) => ({
                          value: cc.id,
                          label: cc.name,
                        }))}
                    />
                  )}
                />
              </Field>

              <Field
                label="Solicitante"
                required
                invalid={!!errors.productApplicant}
                errorText={errors.productApplicant?.message?.toString()}
              >
                <Controller
                  name="productApplicant"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Selecciona solicitante"
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      options={applicants
                        .filter((a) => a.enabled)
                        .map((a) => ({
                          value: a.id,
                          label: a.name,
                        }))}
                    />
                  )}
                />
              </Field>
            </SimpleGrid>
          </FormSection>

          <FormSection
            title="Orden de trabajo"
            hint="Opcional. Si no aplica, deja la OT vacía."
            compact={compact}
          >
            <SimpleGrid columns={fieldColumns} gap={5}>
              <Field
                label="Orden de trabajo"
                invalid={!!errors.workOrder}
                errorText={errors.workOrder?.message?.toString()}
              >
                <Controller
                  name="workOrder"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value ?? undefined}
                      placeholder="Selecciona orden de trabajo"
                      isClearable
                      options={workOrder
                        ?.filter((cc) => cc.enabled)
                        .map((cc) => ({
                          value: cc.id,
                          label: cc.code + "-" + cc.name,
                        }))}
                      onChange={(value) => {
                        handleOnChangeWorkOrder(value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Field>

              <Field
                label="Empresa mandante"
                required
                invalid={!!errors.clientEntity}
                errorText={errors.clientEntity?.message?.toString()}
              >
                <Controller
                  name="clientEntity"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Selecciona empresa mandante"
                      isDisabled={!!workOrderValue}
                      options={clientEntities?.map((clientEntity) => ({
                        value: clientEntity.id,
                        label: clientEntity.name,
                      }))}
                    />
                  )}
                />
              </Field>

              <Field
                label="Actividad"
                required
                invalid={!!errors.activityLabel}
                errorText={errors.activityLabel?.message?.toString()}
              >
                <Controller
                  name="activityLabel"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Selecciona actividad"
                      options={activityLabels
                        ?.filter((al) => al.enabled)
                        .map((al) => ({
                          value: al.id,
                          label: al.label,
                        }))}
                    />
                  )}
                />
              </Field>

              <Field
                label="Equipo"
                invalid={!!errors.equipmentInstance}
                errorText={errors.equipmentInstance?.message?.toString()}
              >
                <Controller
                  name="equipmentInstance"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Selecciona equipo"
                      options={baseEquipmentInstances?.map((e) => ({
                        value: e.instance.id,
                        label: e.instance?.code,
                      }))}
                    />
                  )}
                />
              </Field>
            </SimpleGrid>
          </FormSection>

          <FormSection
            title="Productos"
            hint="Agrega por búsqueda, código de kit o alias."
            compact={compact}
            actions={
              <HStack gap={2} flexWrap="wrap" justify="flex-end">
                <Button
                  variant="outline"
                  fontWeight="normal"
                  color="fg.muted"
                  justifyContent="flex-start"
                  w={{ base: "full", sm: "300px" }}
                  onClick={() => setSearchProductModal(true)}
                >
                  <LuSearch />
                  Buscar producto...
                </Button>

                <PopoverRoot>
                  <PopoverTrigger asChild>
                    <Button variant="outline">
                      <LuScanLine />
                      Código kit
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent w="320px">
                    <PopoverArrow />
                    <PopoverBody>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Agregar productos de un kit
                      </Text>
                      <Select
                        options={kits?.map((k) => ({
                          value: k.id,
                          label: k.name,
                        }))}
                        onChange={(value) => {
                          const kit = kits?.find((k) => k.id === value?.value);
                          if (!kit) return;
                          const productsToAdd = kit.products.map((p) => ({
                            data: p,
                            amount: p.kitProduct.quantity || 1,
                          }));
                          handleAddMultipleProducts(productsToAdd);
                        }}
                        placeholder="Selecciona kit"
                      />
                    </PopoverBody>
                  </PopoverContent>
                </PopoverRoot>

                <PopoverRoot>
                  <PopoverTrigger asChild>
                    <Button variant="outline">
                      <LuTag />
                      Alias
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent w="320px">
                    <PopoverArrow />
                    <PopoverBody>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Agregar producto por alias
                      </Text>
                      <Select
                        options={aliasProducts?.map((ap) => ({
                          value: ap.id,
                          label: ap.name,
                        }))}
                        placeholder="Selecciona alias de producto"
                        onChange={(value) => {
                          const aliasProduct = aliasProducts
                            ?.filter((ap) => ap.enabled)
                            .find((ap) => ap.id === value?.value);
                          if (!aliasProduct) return;
                          handleAddProduct({
                            data: aliasProduct.product,
                            amount: 1,
                          });
                        }}
                      />
                    </PopoverBody>
                  </PopoverContent>
                </PopoverRoot>
              </HStack>
            }
          >
            <ProductsTable
              products={selectedProducts}
              onChange={(products) => {
                handleEditProduct(products);
              }}
              options={{
                amount: true,
                missingStock: true,
                comment: true,
                price: true,
              }}
              emptyText="Busca un producto para agregarlo al vale"
            />

            {errors.products && (
              <Text color="fg.error" fontSize="sm" mt={2}>
                {errors.products.message?.toString()}
              </Text>
            )}
          </FormSection>

          <FormSection
            title="Comentario"
            hint="Visible para quien valide el vale."
            compact={compact}
          >
            <Textarea
              {...register("comment")}
              placeholder="Opcional"
              rows={3}
              resize="vertical"
            />
          </FormSection>
        </Box>

        <Flex
          position="sticky"
          bottom={0}
          mt={compact ? 4 : "auto"}
          mx={compact ? -6 : { base: -3, md: -8 }}
          px={compact ? 6 : { base: 3, md: 8 }}
          py={4}
          bg="bg.panel"
          borderTopWidth="1px"
          borderColor="border"
          align="center"
          justify="space-between"
          gap={4}
          wrap="wrap"
          zIndex={5}
        >
          <Text fontSize="sm" color="fg.muted">
            {selectedProducts.length}{" "}
            {selectedProducts.length === 1 ? "producto" : "productos"} · Total{" "}
            <Text as="span" fontWeight="semibold" color="fg">
              {formatCLP(total)}
            </Text>
          </Text>
          <HStack gap={3}>
            {onCancel && (
              <Button variant="outline" onClick={onCancel} type="button">
                Cancelar
              </Button>
            )}
            <Button
              colorPalette="brand"
              loading={isSubmitting}
              type="submit"
              minW="140px"
            >
              {sumbitButtonText}
            </Button>
          </HStack>
        </Flex>
      </chakra.form>

      {confirmBeforeSubmit && (
        <VoucherSummaryDialog
          isOpen={summaryOpen}
          onClose={() => setSummaryOpen(false)}
          onConfirm={handleConfirmSummary}
          values={getValues()}
          deliverName={deliverName}
          products={selectedProducts}
          total={formatCLP(total)}
        />
      )}

      <ModalSearchProduct
        isOpen={searchProductModal}
        onClose={() => setSearchProductModal(false)}
        selected={selectedProducts}
        onChange={(product) => handleAddProduct(product)}
        storageId={storageValue?.value || undefined}
      />

      <ModalProductScanner
        isOpen={scannerModal}
        onClose={() => setScannerModal(false)}
        onScan={handleScanProduct}
      />
    </Fragment>
  );
};
